/**
 * HIPAA BAA acceptance — the dialog an Administrator sees on the first
 * sign-in to an account that has not accepted the agreement yet.
 *
 * Restyled onto the design-system Dialog (2026-09-08). Behaviour is the
 * legacy dialog's, kept 1:1: cannot be dismissed by backdrop or Escape
 * (the only ways out are Cancel and Accept), Accept is disabled until
 * the checkbox is ticked, and the text is the agreement markdown
 * rendered as-is.
 */
import { useState } from 'react';
import { useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import {
  Button,
  Checkbox,
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogFooterActions,
  DialogHeader,
  DialogTitle,
  DialogTitleBlock,
  styled,
} from '@shared/ui';
import { getTransitionWithVariable } from 'redux_store';
import { HIPAA_AGREEMENT_MARKDOWN } from '../model/agreementText';

const Body = styled('div')({
  padding: '20px 24px',
  background: 'var(--qhs-bg)',
});

// The markdown is headings, paragraphs and a little bold. Styled here
// rather than through a global stylesheet so the agreement reads as a
// document inside the dialog without leaking rules to the rest of the
// app.
const Agreement = styled('div')({
  color: 'var(--qhs-text-1)',
  fontSize: 13,
  lineHeight: 1.6,
  '& h3': {
    margin: '0 0 12px',
    fontFamily: 'var(--qhs-font-head)',
    fontSize: 16,
    fontWeight: 700,
  },
  '& h4': {
    margin: '20px 0 8px',
    fontFamily: 'var(--qhs-font-head)',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '0.2px',
    color: 'var(--qhs-text-2)',
  },
  '& p': { margin: '0 0 10px' },
  '& strong': { fontWeight: 700 },
});

const Consent = styled('div')({
  marginTop: 16,
  paddingTop: 16,
  borderTop: '1px solid var(--qhs-border)',
});

const HipaaAgreementDialog = ({ open, onClose, onSubmit, accountName }) => {
  const { dialogs, btn } = useSelector(state => state.i18n);
  const [isChecked, setChecked] = useState(false);

  return (
    <Dialog
      open={open}
      // Deliberately a no-op: the agreement is left only through the
      // two buttons. Backdrop and Escape are blocked below as well, so
      // the legacy Modal never even asks.
      onClose={() => {}}
      disableBackdropClick
      disableEscapeKeyDown
      maxWidth={900}
      ariaLabel={dialogs.hipaaAgreement}
      header={
        <DialogHeader>
          <DialogTitleBlock>
            <DialogTitle>
              {getTransitionWithVariable(dialogs.hipaaAgreementFor, { accountName })}
            </DialogTitle>
            <DialogDescription>{dialogs.scrollToTheBottom}</DialogDescription>
          </DialogTitleBlock>
        </DialogHeader>
      }
      footer={
        <DialogFooter>
          <DialogFooterActions>
            <Button variant="ghost" onClick={onClose}>
              {btn.cancel}
            </Button>
            <Button variant="primary" onClick={onSubmit} disabled={!isChecked}>
              {btn.acceptContinue}
            </Button>
          </DialogFooterActions>
        </DialogFooter>
      }
    >
      <Body>
        <Agreement>
          <ReactMarkdown>{HIPAA_AGREEMENT_MARKDOWN}</ReactMarkdown>
        </Agreement>
        <Consent>
          <Checkbox checked={isChecked} onChange={e => setChecked(e.target.checked)}>
            {dialogs.readAndAccept}
          </Checkbox>
        </Consent>
      </Body>
    </Dialog>
  );
};

export default HipaaAgreementDialog;
